/**
 * EventRail — the desktop sticky card: fee, window, and the way in.
 *
 * The fee is drawn as BOTH states — the paid line only when the event charges,
 * and "Free" always — because eventDetail.css hides "Free" whenever a paid line
 * is present (`.evb-card:has(.evb-fee--paid) .evb-fee--free`). That is the
 * stylesheet as exported, kept verbatim, so the markup keeps its half of the
 * bargain rather than the rule being rewritten underneath it.
 *
 * REGISTER IS THE ACTION ITSELF, not a link to somewhere else. It used to be an
 * anchor down to the entry band, because the band held a form and the one idea
 * of that layout was that the form had a destination. There is no form and no
 * band any more: pressing this opens the entry modal, which is where the
 * remaining questions — which activities, how many places — are answered.
 *
 * It draws nothing at all when entry is not on offer. The button's absence is
 * the signal, decided in registrationRules and passed down as `buttons`, so a
 * closed or invitation-only event leaves the card with its fee and window and
 * no way in — rather than a control that refuses.
 */

import React from 'react';
import RegistrationWindow from './RegistrationWindow';
import { EventActionCompleted } from './EventActionButton';
import {
  PENDING_TITLE, PENDING_BODY, PENDING_MORE_TITLE, PENDING_MORE_BODY,
} from './pendingPayment';

export default function EventRail({
  isPaid, feeAmount, opensAt, closesAt, places,
  registrationType, registrationRequired, externalUrl,
  buttons, isCompleted, onAction, pendingPayment, onBookMore,
}) {
  const available = Array.isArray(buttons) ? buttons : [];
  const register = available.find(b => String(b.id) === 'register');
  const myRegistrations = available.find(b => String(b.id) === 'view_my_registrations');
  const entered = Boolean(register && isCompleted && isCompleted(register));
  // A payment that left for the gateway and has not been heard back on. Ranked
  // BELOW `entered` deliberately: both can be true for a moment — the return
  // page confirms and writes the entered marker while this page still holds the
  // pending one — and "you are in" is the answer with a real row behind it.
  const paying = Boolean(pendingPayment) && !entered;

  return (
    <aside className="evb-rail">
      <div className="evb-card">
        {isPaid && <p className="evb-fee evb-fee--paid">{feeAmount}</p>}
        <p className="evb-fee evb-fee--free">Free</p>
        <p className="evb-fee__k">Entry fee</p>

        <RegistrationWindow
          opensAt={opensAt}
          closesAt={closesAt}
          places={places}
          registrationType={registrationType}
          registrationRequired={registrationRequired}
          externalUrl={externalUrl}
        />

        {entered && <EventActionCompleted button={register} />}

        {/* Said here rather than only on the return page, because this is where
            a runner lands when they close that one — and this is the card
            holding the button that would charge them a second time. */}
        {paying && (
          <div className="evb-paying" role="status">
            <b className="evb-paying__t">{PENDING_TITLE}</b>
            <span className="evb-paying__m">{PENDING_BODY}</span>
          </div>
        )}

        {register && !entered && !paying && (
          <button type="button" className="evb-go" onClick={() => onAction(register)}>
            Register for this event
          </button>
        )}

        {/* Only once they have entered: before that it is a link to an empty
            list, and it competes with the one action this card exists for. */}
        {entered && myRegistrations && (
          <a className="evb-go" href={myRegistrations.href}>
            {myRegistrations.label}
          </a>
        )}

        {/* Secondary to the pass link above, because somebody arriving at an
            event they have entered is far more often looking for their QR than
            buying again — but offered, because there is no rule that one
            person holds one place, and before this the only way to book a
            second seat was to not have booked the first.

            `pendingPayment` excludes it for the same reason the Register
            button is suppressed: the card must not offer another checkout
            while money from the last one is still unaccounted for.

            TESTED AGAINST THE PROP, NOT `paying`. That flag is
            `pendingPayment && !entered`, so inside a branch that requires
            `entered` it is always false and would have guarded nothing. The
            state it has to catch is exactly the one it could not see:
            somebody who holds an entry AND has a second checkout in flight,
            which is the only way to reach this button with a payment
            outstanding. */}
        {/* THE OTHER HALF OF THAT SUPPRESSION, and the reason this block
            exists: hiding the link was right, but it left the card silently
            dropping a control it had just been offering, which reads as a
            fault rather than a state. Said here, in the link's own place, so
            the answer is where the question was.

            Its own words, never the pair above: this buyer IS registered —
            the line directly above says so — and only the extra seats are
            unresolved. Reusing the not-entered copy would tell somebody who
            holds a confirmed entry that their registration is still being
            confirmed. */}
        {entered && pendingPayment && register && onBookMore && (
          <div className="evb-paying" role="status">
            <b className="evb-paying__t">{PENDING_MORE_TITLE}</b>
            <span className="evb-paying__m">{PENDING_MORE_BODY}</span>
          </div>
        )}

        {entered && !pendingPayment && register && onBookMore && (
          /*
           * A LINK, NOT A BUTTON, on purpose: the card already has one filled
           * action and a second block of the same weight competed with it.
           * Still a <button> element, because it opens the entry pop-up rather
           * than going anywhere -- an <a> with no href is not a link to a
           * screen reader, and one with a fake href breaks middle-click.
           */
          <button
            type="button"
            className="evb-link evb-link--btn"
            onClick={() => onBookMore(register)}
          >
            Book more seats
          </button>
        )}
        {/* The organiser's site is still one click away in the Organiser
            section below; a second link to it here competed with Register for
            the one action this card exists to offer. */}
      </div>
    </aside>
  );
}
